/**
 * Team Management Service
 * Part of TASK-334: Frontend Admin Panel
 *
 * Handles team CRUD operations, member management, and client assignments
 * for the admin panel.
 */

import { httpClient } from '../../lib/httpClient';
import type {
  Team,
  TeamDetail,
  TeamListResponse,
  TeamCreateData,
  TeamUpdateData,
  TeamMemberSummary,
  TeamMemberAddData,
  TeamClientAssignment,
  TeamMemberRole,
} from '../../types/admin';

class TeamService {
  private readonly basePath = '/api/admin/teams';

  /**
   * List all teams with optional filtering
   */
  async listTeams(params?: {
    is_active?: boolean;
    search?: string;
  }): Promise<TeamListResponse> {
    try {
      const data = await httpClient.get<TeamListResponse>(this.basePath, { params });
      return data;
    } catch (error) {
      console.error('Error listing teams:', error);
      throw error;
    }
  }

  /**
   * Get team details including members
   */
  async getTeam(teamId: string): Promise<TeamDetail> {
    try {
      const data = await httpClient.get<TeamDetail>(`${this.basePath}/${teamId}`);
      return data;
    } catch (error) {
      console.error('Error fetching team:', error);
      throw error;
    }
  }

  /**
   * Create a new team
   */
  async createTeam(teamData: TeamCreateData): Promise<Team> {
    try {
      const data = await httpClient.post<Team>(this.basePath, teamData);
      return data;
    } catch (error) {
      console.error('Error creating team:', error);
      throw error;
    }
  }

  /**
   * Update team information
   */
  async updateTeam(teamId: string, updates: TeamUpdateData): Promise<Team> {
    try {
      const data = await httpClient.put<Team>(`${this.basePath}/${teamId}`, updates);
      return data;
    } catch (error) {
      console.error('Error updating team:', error);
      throw error;
    }
  }

  /**
   * Delete a team
   */
  async deleteTeam(
    teamId: string,
    reassignToTeamId?: string
  ): Promise<{ message: string }> {
    try {
      const data = await httpClient.delete<{ message: string }>(
        `${this.basePath}/${teamId}`,
        { data: reassignToTeamId ? { reassign_to_team_id: reassignToTeamId } : undefined }
      );
      return data;
    } catch (error) {
      console.error('Error deleting team:', error);
      throw error;
    }
  }

  // ==================== Member Management ====================

  /**
   * Get team members
   */
  async getTeamMembers(teamId: string): Promise<TeamMemberSummary[]> {
    try {
      const data = await httpClient.get<{ members: TeamMemberSummary[] }>(
        `${this.basePath}/${teamId}/members`
      );
      return data.members;
    } catch (error) {
      console.error('Error fetching team members:', error);
      throw error;
    }
  }

  /**
   * Add a member to a team
   */
  async addTeamMember(teamId: string, memberData: TeamMemberAddData): Promise<TeamMemberSummary> {
    try {
      const data = await httpClient.post<TeamMemberSummary>(
        `${this.basePath}/${teamId}/members`,
        memberData
      );
      return data;
    } catch (error) {
      console.error('Error adding team member:', error);
      throw error;
    }
  }

  /**
   * Remove a member from a team
   */
  async removeTeamMember(teamId: string, userId: string): Promise<{ message: string }> {
    try {
      const data = await httpClient.delete<{ message: string }>(
        `${this.basePath}/${teamId}/members/${userId}`
      );
      return data;
    } catch (error) {
      console.error('Error removing team member:', error);
      throw error;
    }
  }

  /**
   * Change a member's role within the team
   */
  async changeTeamMemberRole(
    teamId: string,
    userId: string,
    role: TeamMemberRole
  ): Promise<TeamMemberSummary> {
    try {
      const data = await httpClient.put<TeamMemberSummary>(
        `${this.basePath}/${teamId}/members/${userId}/role`,
        { role }
      );
      return data;
    } catch (error) {
      console.error('Error changing team member role:', error);
      throw error;
    }
  }

  // ==================== Client Assignments ====================

  /**
   * Get clients assigned to a team
   */
  async getTeamClients(
    teamId: string,
    params?: { page?: number; page_size?: number }
  ): Promise<{ clients: TeamClientAssignment[]; total: number }> {
    try {
      const data = await httpClient.get<{ clients: TeamClientAssignment[]; total: number }>(
        `${this.basePath}/${teamId}/clients`,
        { params }
      );
      return data;
    } catch (error) {
      console.error('Error fetching team clients:', error);
      throw error;
    }
  }

  /**
   * Assign a client to a team
   */
  async assignClientToTeam(
    teamId: string,
    clientId: string
  ): Promise<TeamClientAssignment> {
    try {
      const data = await httpClient.post<TeamClientAssignment>(
        `${this.basePath}/${teamId}/clients`,
        { client_id: clientId }
      );
      return data;
    } catch (error) {
      console.error('Error assigning client to team:', error);
      throw error;
    }
  }

  /**
   * Remove client assignment from team
   */
  async unassignClientFromTeam(
    teamId: string,
    clientId: string
  ): Promise<{ message: string }> {
    try {
      const data = await httpClient.delete<{ message: string }>(
        `${this.basePath}/${teamId}/clients/${clientId}`
      );
      return data;
    } catch (error) {
      console.error('Error unassigning client from team:', error);
      throw error;
    }
  }

  // ==================== Bulk Operations ====================

  /**
   * Bulk add members to a team
   */
  async bulkAddMembers(
    teamId: string,
    userIds: string[],
    role: TeamMemberRole = 'member'
  ): Promise<{ added: number; failed: string[] }> {
    try {
      const data = await httpClient.post<{ added: number; failed: string[] }>(
        `${this.basePath}/${teamId}/members/bulk`,
        { user_ids: userIds, role }
      );
      return data;
    } catch (error) {
      console.error('Error bulk adding members:', error);
      throw error;
    }
  }

  /**
   * Bulk assign clients to a team
   */
  async bulkAssignClients(
    teamId: string,
    clientIds: string[]
  ): Promise<{ assigned: number; failed: string[] }> {
    try {
      const data = await httpClient.post<{ assigned: number; failed: string[] }>(
        `${this.basePath}/${teamId}/clients/bulk`,
        { client_ids: clientIds }
      );
      return data;
    } catch (error) {
      console.error('Error bulk assigning clients:', error);
      throw error;
    }
  }
}

export const teamService = new TeamService();
